import { type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { useModalTransition } from "@/hooks/useModalTransition";

type Props = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** id pour aria-labelledby sur le panneau */
  labelledBy?: string;
  panelClassName?: string;
  /** bottom = sheet mobile, center = modale centrée, full = page plein écran */
  variant?: "center" | "bottom" | "full";
  /** slide = entrée verticale, fade = fondu simple */
  animation?: "slide" | "fade";
  /** transparent = pas de fond assombri (ex. modale empilée sur le compose) */
  backdrop?: "default" | "transparent" | "stacked";
};

/**
 * Overlay modale — toujours en portal sur document.body.
 * Sinon (rendu dans le header z-40) la modale reste prisonnière du stacking
 * context et passe derrière le workspace mail.
 */
export function MailModalOverlay({
  open,
  onClose,
  children,
  labelledBy,
  panelClassName,
  variant = "center",
  animation = "slide",
  backdrop = "default",
}: Props) {
  void onClose;
  const { mounted, visible } = useModalTransition(open);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[300] overflow-x-hidden overflow-y-auto overscroll-contain",
        backdrop === "default" && "mail-modal-backdrop bg-black/40 backdrop-blur-[2px]",
        backdrop === "stacked" && "mail-modal-backdrop bg-black/22",
        backdrop === "transparent" && "pointer-events-none bg-transparent",
        visible && (backdrop === "default" || backdrop === "stacked") && "mail-modal-backdrop-visible"
      )}
      role="presentation"
    >
      <div
        className={cn(
          "flex min-h-full w-full",
          variant === "bottom"
            ? "items-end justify-center sm:items-center sm:p-4"
            : variant === "full"
              ? "items-stretch justify-stretch p-0"
              : "items-center justify-center p-2 sm:p-4"
        )}
      >
        <div
          className={cn(
            "mail-modal-panel mail-panel overflow-hidden shadow-[var(--mail-shadow-md)]",
            backdrop === "transparent" && "pointer-events-auto",
            backdrop === "stacked" && "pointer-events-auto mail-modal-panel--stacked",
            animation === "fade" ? "mail-modal-panel-fade" : null,
            visible && "mail-modal-panel-visible",
            variant === "bottom" && "mail-modal-panel-bottom w-full max-w-2xl",
            variant === "center" && "w-full max-w-md",
            variant === "full" &&
              "h-[100dvh] w-full max-w-none !rounded-none border-0 shadow-none",
            panelClassName
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
